import Button from 'components/Button'
import FullPageLoading from 'components/FullPageLoading'
import Icon from 'components/Icon'
import constants from 'constants/constants'
import DateField from 'features/forms/components/DateField'
import SelectField from 'features/forms/components/SelectField'
import TextField from 'features/forms/components/TextField'
import { showErrorNotification, showNotification } from 'features/notifications/notificationsSlice'
import { Form, Formik, FormikProps } from 'formik'
import { CreateEvent as CreateEventInput, MyOrganizationFragment } from 'generated/graphql'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import routes from 'routes'
import styled from 'styled-components'
import { colors, withCapture, _ } from 'utils'
import * as Yup from 'yup'
import { createEvent } from './graphql/mutation'

const S = {
    BackToList: styled.div`
        position: absolute;
        top: -40px;
        left: 8px;
        display: flex;
        align-items: center;
        cursor: pointer;
        color: ${colors.icon};
        font-size: 14px;
        &:hover {
            filter: brightness(80%);
        }
    `,
    Icon: styled(Icon)`
        transform: rotate(90deg);
    `,
    Header: styled.h3`
        width: 100%;
        margin: -10px 10px 20px 10px;
        font-weight: 500;
        font-size: 30px;
        color: ${colors.dark};
    `,
    Actions: styled.div`
        width: 100%;
        padding: 0 10px;
        display: flex;
        justify-content: flex-end;
    `
}

const initialValues = {
    name: '',
    slug: '',
    startDate: new Date(),
}

const createEventValidation = constants.isLite ? (
    Yup.object().shape({
        name: Yup.string().required(),
        startDate: Yup.date(),
    })
) : (
    Yup.object().shape({
        name: Yup.string().required(),
        slug: Yup
            .string()
            .matches(/^[a-z0-9_-]+$/, _('form.validation.invalid_slug', {}, false)),
        startDate: Yup.date(),
    })
)

interface Props {
    close: () => void
    closeListModal?: () => void
    fetchData?: () => void
    organizations: MyOrganizationFragment[]
}
const CreateEvent = ({
    close, closeListModal, fetchData, organizations
}: Props) => {
    const [isUploading, setIsUploading] = useState(false)
    const dispatch = useDispatch()
    const history = useHistory()

    const handleSubmit = withCapture(async (values) => {
        setIsUploading(true)
        const response = await createEvent(values)
        setIsUploading(false)
        if (!response.data || response.errors.length) {
            dispatch(showErrorNotification(response?.errors))
            return
        }
        dispatch(showNotification({
            type: 'success',
            body: _('create_event.success')
        }))
        await fetchData?.()
        history.push(routes.generalSettings.getPath({ eventSlug: response.data?.createEvent?.slug }))
        closeListModal?.()
        close()
    })

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={createEventValidation}
            onSubmit={handleSubmit}
        >
            {(props: FormikProps<CreateEventInput>) => (
                <Form className="row position-relative">
                    <S.BackToList onClick={close}>
                        <S.Icon icon="chevronDown" size={20} />
                        {_('create_event.back_to_event_list')}
                    </S.BackToList>
                    <S.Header>
                        {_('create_event.header')}
                    </S.Header>
                    <TextField
                        name="name"
                        label={_('create_event.form.name')}
                        isRequired
                        showInfoIcon
                        helperText={_('create_event.form.name_helper_text')}
                        lg={1/2}
                    />
                    {
                        !constants.isLite && (
                            <TextField
                                name="slug"
                                label={_('create_event.form.slug')}
                                showInfoIcon
                                helperText={_('create_event.form.slug_helper_text')}
                                lg={1/2}
                            />
                        )
                    }
                    <DateField
                        name="startDate"
                        label={_('create_event.form.start_date')}
                        showInfoIcon
                        helperText={_('create_event.form.start_date_helper_text')}
                        shouldCloseOnSelect={false}
                        lg={1/2}
                    />
                    <SelectField
                        className={constants.isLite ? 'd-block' : 'd-none'}
                        name="organizationId"
                        label={_('create_event.form.organization')}
                        placeholder={_('create_event.form.organization_placeholder')}
                        helperText={_('create_event.form.organization_helper_text')}
                        options={organizations}
                        optionLabel="name"
                        optionValue="id"
                        defaultValue={organizations.length === 1 ? organizations[0] : null}
                        isClearable={false}
                        isRequired
                    />
                    <S.Actions>
                        <Button type="submit" disabled={!(!constants.isLite || props.values.organizationId)}>
                            {_('create_event.form.submit_button')}
                        </Button>
                    </S.Actions>
                    {isUploading && <FullPageLoading isAbsolute fullWidth />}
                </Form>
            )}
        </Formik>
    )
}

export default CreateEvent
